import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import LoginPage from "@/pages/LoginPage";
import VerifyOtpPage from "@/pages/VerifyOtpPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import DeleteAccountRequestPage from "@/pages/DeleteAccountRequestPage";
import DashboardPage from "@/pages/DashboardPage";
import UserManagementPage from "@/pages/UserManagementPage";
import LinkManagementPage from "@/pages/LinkManagementPage";
import MyLinksPage from "@/pages/MyLinksPage";
import LinkViewerPage from "@/pages/LinkViewerPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import ProfilePage from "@/pages/ProfilePage";
import AdminSettingsPage from "@/pages/AdminSettingsPage";
import SubscriptionConfigPage from "@/pages/SubscriptionConfigPage";
import AdminTokensPage from "@/pages/AdminTokensPage";
import AuditLogsPage from "@/pages/AuditLogsPage";
import TicketsPage from "@/pages/TicketsPage";
import BillingPage from "@/pages/BillingPage";
import IntegratorPage from "@/pages/IntegratorPage";
import InviteAcceptPage from "@/pages/InviteAcceptPage";
import NotificationsPage from "@/pages/NotificationsPage";
import PublicSubscriptionPage from "@/pages/PublicSubscriptionPage";
import RedeemTokenPage from "@/pages/RedeemTokenPage";

function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/verify-otp" element={<VerifyOtpPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/delete-account" element={<DeleteAccountRequestPage />} />
      <Route path="/subscription/ios" element={<PublicSubscriptionPage />} />
      <Route path="/redeem/:token" element={<RedeemTokenPage />} />
      <Route path="/redeem" element={<RedeemTokenPage />} />
      <Route path="/invite/:token" element={<InviteAcceptPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <AppLayout><DashboardPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/users" element={
        <ProtectedRoute>
          <AppLayout><UserManagementPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/links" element={
        <ProtectedRoute>
          <AppLayout><LinkManagementPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/my-links" element={
        <ProtectedRoute>
          <AppLayout><MyLinksPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/view/:linkId" element={
        <ProtectedRoute>
          <AppLayout><LinkViewerPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/subscription" element={
        <ProtectedRoute>
          <AppLayout><SubscriptionPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute>
          <AppLayout><AdminSettingsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/subscription-config" element={
        <ProtectedRoute>
          <AppLayout><SubscriptionConfigPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/tokens" element={
        <ProtectedRoute>
          <AppLayout><AdminTokensPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/admin/audit" element={
        <ProtectedRoute>
          <AppLayout><AuditLogsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/tickets" element={
        <ProtectedRoute>
          <AppLayout><TicketsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/notifications" element={
        <ProtectedRoute>
          <AppLayout><NotificationsPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/billing" element={
        <ProtectedRoute>
          <AppLayout><BillingPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/team" element={
        <ProtectedRoute>
          <AppLayout><IntegratorPage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <AppLayout><ProfilePage /></AppLayout>
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="bottom-right" richColors />
        {/* Made by Intellis */}
        <a
          href="https://www.intellis.it"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            position: 'fixed',
            bottom: '16px',
            right: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            backgroundColor: 'rgba(26, 26, 26, 0.85)',
            backdropFilter: 'blur(8px)',
            borderRadius: '8px',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '11px',
            textDecoration: 'none',
            zIndex: 9999,
            transition: 'all 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
          }}
          data-testid="made-by-intellis"
        >
          <img
            src="/intellis-logo.png"
            alt="Intellis"
            style={{ width: '16px', height: '16px', borderRadius: '3px' }}
          />
          Made by Intellis Srl
        </a>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
